import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  OnDestroy,
} from '@angular/core';
import { ReportPrintData } from '../report-manager.interface';
import htmlToPdfmake from 'html-to-pdfmake';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { Subject } from 'rxjs';
import { UserDatePipe } from '../../../../../../shared/pipes/user-date.pipe';
import { CommonService } from '../../../../../../shared/services/common.service';

@Component({
  selector: 'app-print-report',
  standalone: true,
  imports: [CommonModule, UserDatePipe],
  templateUrl: './print-report.component.html',
})
export class PrintReportComponent implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  printData: any = null;
  signatures = new Array(10);
  /* eslint-disable @typescript-eslint/no-explicit-any */
  @Input() set reportData(val: any) {
    this.printData = val;

    this.cd.detectChanges();
    this.initialized = true;
  }
  @ViewChild('pdfContent', { static: true }) pdfContent!: ElementRef;
  initialized = false;
  private readonly destroy$ = new Subject<void>();

  base64 = '';

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly commonService: CommonService
  ) {
    (pdfMake.vfs as any) = pdfFonts;
  }
  async printPDF() {
    if (this.printData) {
      const pdfContent = document.getElementById('pdf-data');
      if (pdfContent) {
        const pdfMakeContent = htmlToPdfmake(pdfContent.innerHTML);
        const pdfData = this.printData;

        const logoBase64 = pdfData?.organizationDetail?.logoUrl
          ? await this.commonService.getBase64ImageFromUrl(
              pdfData.organizationDetail.logoUrl
            )
          : await this.commonService.getBase64ImageFromUrl(
              '/assets/images/logo.png'
            );

        const columns: any[] = [
          logoBase64
            ? {
                image: logoBase64,
                width: 125,
                fit: [125, 40], // Fit the image within 125x40 dimensions (reduces height)
                alignment: 'left',
              }
            : {},
          {
            stack: [
              `${pdfData?.organizationDetail?.address || ''}, ${
                pdfData?.organizationDetail?.city || ''
              }`,
              `${pdfData?.organizationDetail?.state || ''}, ${
                pdfData?.organizationDetail?.country || ''
              }, ${pdfData?.organizationDetail?.zipCode || ''}`,
            ],
            fontSize: 10,
            alignment: 'right',
          },
        ];
        // Define the PDF document
        const documentDefinition: TDocumentDefinitions = {
          content: pdfMakeContent,
          pageMargins: [20, 60, 20, 40], // Adjusted margins
          header: (currentPage, pageCount, pageSize) => {
            const pageWidth =
              typeof pageSize === 'object' ? pageSize.width : 595.28; // Default A4 width
            const lineWidth = 552; // Explicit width

            return [
              {
                margin: [20, 10, 20, 0], // Left, Top, Right, Bottom
                columns: columns as any,
              },
              {
                // Horizontal Line Below Header
                canvas: [
                  {
                    type: 'line',
                    x1: (pageWidth - lineWidth) / 2, // Center the line
                    y1: 0,
                    x2: (pageWidth - lineWidth) / 2 + lineWidth, // End point
                    y2: 0,
                    lineWidth: 1, // Thickness
                    lineColor: '#eaecf0', // Light gray color
                  },
                ],
                margin: [0, 5, 0, 8], // Increase bottom margin to **20** to create space
              },
            ];
          },
          footer: function (currentPage: number) {
            return {
              columns: [
                { text: '', alignment: 'left' },
                {
                  text: `Powered by Insights to Behavior®`,
                  alignment: 'center',
                },
                { text: `${currentPage}`, alignment: 'right' },
              ],
              margin: [10, 20, 10, 0],
              color: '#475467',
            };
          },
          styles: {
            desc: { fontSize: 12 },
            address: { fontSize: 12 },
            title: { fontSize: 12 },
            datalabel: { color: '#475467' },
            dataValue: { color: '#475467' },
            commentLabel: {
              lineHeight: 1.3,
              color: '#475467',
              fontSize: 9,
            },
            extraLineHeight: {
              lineHeight: 1.5,
            },
            font10: {
              fontSize: 12,
              color: '#475467',
            },
            chartImage: {
              alignment: 'center',
            },
          },
          defaultStyle: {
            columnGap: 10,
            fontSize: 10,
          },
        };

        // Generate and download the PDF
        pdfMake
          .createPdf(documentDefinition)
          .download(
            `Report_${this.printData?.caseData.student.firstName}_${this.printData?.caseData.student.lastName}.pdf`
          );

        this.initialized = false;
        this.ngOnDestroy();
      }
    }
  }
  public hasGraphContent(report: ReportPrintData): boolean {
    return (
      report.graphs?.some(
        g =>
          (g.graphImage?.length ?? 0) > 0 ||
          (g.graphTotalImage?.length ?? 0) > 0 ||
          (g.commentsData?.length ?? 0) > 0
      ) ?? false
    );
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
